import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { DEFAULT_TIME_RANGE } from '../../../config/constants';
import type { IBalanceEntryForCashFlowProjection } from '../../../interfaces/projection';
import { useClient } from '../../client-api';
import type { CashFlowDataPointDto, CashFlowProjectionDto, CashFlowType, ItemSettingsDto, LifeEventDto, SurplusType } from '../../generated/client';
import { generateCashFlowForClient } from '../../generated/client';
import { useGeneralProjectionSettings, useProjectionCashEventSettings, useProjectionCashFlowSettings } from '../../projection-settings-api';
import { queryKeys } from '../../query-keys';
import { useSurplusSettings } from '../../surplus-api';

export function useCashFlow(): UseQueryResult<CashFlowDataPointDto[], AxiosError> {
    const { data: client } = useClient();
    const { data: cashFlows } = useProjectionCashFlowSettings();
    const { data: cashEvents } = useProjectionCashEventSettings();
    const { data: surplus } = useSurplusSettings();
    const { data: generalSettings } = useGeneralProjectionSettings();
    const cashFlow = surplus?.enabled ? surplus?.itemsSettings : cashFlows?.itemsSettings;

    const enabledCashEvents: LifeEventDto[] = cashEvents?.enabled
        ? cashEvents?.itemsSettings
              .filter((cashEvent) => cashEvent.enabled)
              .map((enabledCashEvent) => ({
                  type: (enabledCashEvent.amount! >= 0 ? 'INFLOW' : 'OUTFLOW') as CashFlowType,
                  amount: Math.abs(enabledCashEvent.amount!),
                  dueDate: enabledCashEvent.effectiveDate ?? '',
              }))
        : [];

    const getSurplusType = (cashFlow: ItemSettingsDto): SurplusType => {
        return surplus?.enabled && cashFlow.surplusType ? cashFlow.surplusType : cashFlow.amount! >= 0 ? 'INCOME' : 'EXPENSE';
    };

    const enabledCashFlows: IBalanceEntryForCashFlowProjection[] =
        cashFlow && cashFlows?.enabled
            ? cashFlow
                  .filter((cashflow) => cashflow.enabled)
                  .map((enabledCashFlow) => ({
                      accountType: getSurplusType(enabledCashFlow),
                      amount: Math.abs(enabledCashFlow.amount!),
                      fromDate: enabledCashFlow.fromDate ?? '',
                      toDate: enabledCashFlow.toDate ?? '',
                      refinancingDetails: {
                          refinancingEligible: false,
                          refinancingRequested: false,
                      },
                  }))
            : [];

    return useQuery({
        queryKey: queryKeys.cashFlow(client?.partnerUuid, {
            balanceEntryDTOList: enabledCashFlows,
            lifeEventDTOList: enabledCashEvents,
            range: generalSettings?.projectionPeriodYears ? generalSettings.projectionPeriodYears : DEFAULT_TIME_RANGE,
        }),
        queryFn: ({ queryKey }) => {
            const payload: CashFlowProjectionDto = queryKey[3] as CashFlowProjectionDto;

            return generateCashFlowForClient({ path: { clientId: client!.id }, body: payload }).then((result) => result.data);
        },
        enabled: !!client && !!cashFlows && !!cashEvents && !!surplus && !!generalSettings,
    });
}
